<template>
  <div class="attendance-management-page">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading attendance data...</p>
    </div>

    <div v-else>
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="d-flex align-center mb-3">
          <v-btn
            icon
            variant="text"
            @click="$router.go(-1)"
            class="mr-2"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="text-h4 font-weight-bold text-primary">
              Manage Attendance
            </h1>
            <p class="text-body-1 text-medium-emphasis mb-0">
              {{ meeting?.title || 'Meeting' }} - {{ formatDate(meeting?.start_datetime) }}
            </p>
          </div>
        </div>

        <div class="d-flex align-center gap-3">
          <v-btn
            color="primary"
            prepend-icon="mdi-account-plus"
            @click="showAddAttendeeDialog = true"
            :disabled="!attendanceStatus?.can_manage_attendance"
          >
            Add Attendee
          </v-btn>
          
          <v-btn
            color="success"
            variant="outlined"
            prepend-icon="mdi-check-all"
            :disabled="!canMarkAsPresent()"
            @click="bulkMarkPresent"
          >
            Mark All Present
          </v-btn>

          <v-btn
            color="secondary"
            variant="outlined"
            prepend-icon="mdi-export"
            @click="exportAttendance"
          >
            Export
          </v-btn>

          <!-- Close Attendance Button -->
          <v-btn
            v-if="meeting?.status === 'completed' && attendanceStatus?.can_manage_attendance"
            color="error"
            variant="outlined"
            prepend-icon="mdi-lock"
            :loading="closingAttendance"
            @click="closeAttendance"
          >
            Close Attendance
          </v-btn>
        </div>
      </div>

      <!-- Attendance Status Alert -->
      <v-alert
        v-if="attendanceStatus && (!attendanceStatus.can_manage_attendance || attendanceStatus.time_remaining_text)"
        :type="attendanceStatus.attendance_closed ? 'warning' : 'info'"
        :icon="attendanceStatus.attendance_closed ? 'mdi-lock' : 'mdi-clock-outline'"
        class="mb-6"
        prominent
      >
        <template v-if="attendanceStatus.attendance_closed">
          <v-alert-title>Attendance Management Closed</v-alert-title>
          <div class="mt-2">
            {{ getAttendanceStatusMessage() }}
          </div>
        </template>
        <template v-else-if="attendanceStatus.time_remaining_text">
          <v-alert-title>Auto-Close Warning</v-alert-title>
          <div class="mt-2">
            Attendance management will automatically close in {{ attendanceStatus.time_remaining_text }}
            ({{ formatDateTime(attendanceStatus.auto_close_at) }})
          </div>
        </template>
      </v-alert>

      <!-- Summary Cards -->
      <v-row class="mb-6">
        <v-col cols="12" md="3">
          <v-card class="summary-card">
            <v-card-text class="text-center">
              <div class="text-h3 font-weight-bold text-primary">
                {{ attendanceStats.total }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Total Invitees
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="summary-card">
            <v-card-text class="text-center">
              <div class="text-h3 font-weight-bold text-success">
                {{ attendanceStats.present }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Present
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="summary-card">
            <v-card-text class="text-center">
              <div class="text-h3 font-weight-bold text-error">
                {{ attendanceStats.absent }}
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Absent
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="3">
          <v-card class="summary-card">
            <v-card-text class="text-center">
              <div class="text-h3 font-weight-bold text-info">
                {{ Math.round(attendanceStats.rate) }}%
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Attendance Rate
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Attendance Restriction Notice -->
      <v-alert
        v-if="getAttendanceStatusMessage()"
        type="info"
        variant="tonal"
        class="mb-6"
        icon="mdi-information"
      >
        {{ getAttendanceStatusMessage() }}
      </v-alert>

      <!-- Filters -->
      <v-card class="mb-6">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="searchQuery"
                placeholder="Search attendees..."
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="compact"
                clearable
                hide-details
              />
            </v-col>
            
            <v-col cols="12" md="3">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                label="Status"
                variant="outlined"
                density="compact"
                clearable
                hide-details
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-select
                v-model="roleFilter"
                :items="roleOptions"
                label="Role"
                variant="outlined"
                density="compact"
                clearable
                hide-details
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-btn-toggle
                v-model="viewMode"
                mandatory
                density="compact"
                variant="outlined"
              >
                <v-btn value="list" icon>
                  <v-icon>mdi-view-list</v-icon>
                </v-btn>
                <v-btn value="cards" icon>
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Attendance List -->
      <div v-if="viewMode === 'list'">
        <v-card>
          <v-data-table
            :headers="tableHeaders"
            :items="filteredAttendance"
            :loading="loading"
            item-value="id"
            class="elevation-0"
          >
            <template #[`item.user`]="{ item }">
              <div class="d-flex align-center">
                <v-avatar
                  :color="item.user?.avatar ? undefined : 'primary'"
                  size="40"
                  class="mr-3"
                >
                  <v-img
                    v-if="item.user?.avatar"
                    :src="getUserPhotoUrl(item.user)"
                    :alt="item.user?.full_name"
                  />
                  <span v-else class="text-subtitle-2 font-weight-bold">
                    {{ getInitials(item.user?.full_name) }}
                  </span>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">{{ item.user?.full_name }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ item.user?.email }}</div>
                  <div v-if="item.user?.board_member?.position" class="text-caption">
                    {{ item.user?.board_member?.position }}
                  </div>
                </div>
              </div>
            </template>

            <template #[`item.status`]="{ item }">
              <v-select
                :model-value="item.status"
                :items="getFilteredStatusOptions(item.status)"
                variant="outlined"
                density="compact"
                hide-details
                :disabled="!attendanceStatus?.can_manage_attendance"
                @update:model-value="updateAttendanceStatus(item, $event)"
              >
                <template #selection="{ item: statusItem }">
                  <v-chip
                    :color="getStatusColor(statusItem.value)"
                    size="small"
                    variant="flat"
                  >
                    {{ statusItem.title }}
                  </v-chip>
                </template>
              </v-select>
            </template>

            <template #[`item.checked_in_at`]="{ item }">
              <div v-if="item.checked_in_at">
                {{ formatTime(item.checked_in_at) }}
              </div>
              <v-btn
                v-else-if="item.status === 'attending'"
                size="small"
                color="primary"
                variant="outlined"
                :disabled="!canMarkAsPresent()"
                @click="checkInAttendee(item)"
              >
                Check In
              </v-btn>
              <span v-else class="text-medium-emphasis">-</span>
            </template>

            <template #[`item.notes`]="{ item }">
              <v-text-field
                :model-value="item.notes"
                placeholder="Add notes..."
                variant="outlined"
                density="compact"
                hide-details
                :disabled="!attendanceStatus?.can_manage_attendance"
                @blur="updateAttendanceNotes(item, $event.target.value)"
                @keyup.enter="updateAttendanceNotes(item, $event.target.value)"
              />
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex align-center gap-1">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  :disabled="!attendanceStatus?.can_manage_attendance"
                  @click="editAttendance(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  color="error"
                  :disabled="!attendanceStatus?.can_manage_attendance"
                  @click="removeAttendee(item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </div>

      <!-- Attendance Cards -->
      <div v-else class="attendance-grid">
        <v-row>
          <v-col
            v-for="attendance in filteredAttendance"
            :key="attendance.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              class="attendance-card"
              :class="{
                'border-success': attendance.status === 'attending',
                'border-error': attendance.status === 'absent',
                'border-warning': attendance.status === 'late'
              }"
            >
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-3">
                  <v-avatar
                    :color="attendance.user?.avatar ? undefined : 'primary'"
                    size="48"
                    class="mr-3"
                  >
                    <v-img
                      v-if="attendance.user?.avatar"
                      :src="getUserPhotoUrl(attendance.user)"
                      :alt="attendance.user?.full_name"
                    />
                    <span v-else class="text-h6 font-weight-bold">
                      {{ getInitials(attendance.user?.full_name) }}
                    </span>
                  </v-avatar>
                  
                  <div class="flex-grow-1">
                    <div class="font-weight-bold">{{ attendance.user?.full_name }}</div>
                    <div class="text-caption text-medium-emphasis">
                      {{ attendance.user?.board_member?.position || 'Member' }}
                    </div>
                  </div>

                  <v-menu offset-y>
                    <template #activator="{ props }">
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        v-bind="props"
                      >
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list density="compact">
                      <v-list-item @click="editAttendance(attendance)">
                        <v-list-item-title>Edit</v-list-item-title>
                        <template #prepend>
                          <v-icon>mdi-pencil</v-icon>
                        </template>
                      </v-list-item>
                      <v-list-item @click="removeAttendee(attendance)" class="text-error">
                        <v-list-item-title>Remove</v-list-item-title>
                        <template #prepend>
                          <v-icon>mdi-delete</v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <v-select
                  :model-value="attendance.status"
                  :items="getFilteredStatusOptions(attendance.status)"
                  label="Status"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="mb-3"
                  :disabled="!attendanceStatus?.can_manage_attendance"
                  @update:model-value="updateAttendanceStatus(attendance, $event)"
                />

                <div v-if="attendance.checked_in_at" class="mb-3">
                  <div class="text-caption text-medium-emphasis">Checked in:</div>
                  <div class="text-body-2">{{ formatTime(attendance.checked_in_at) }}</div>
                </div>

                <div v-else-if="attendance.status === 'attending'" class="mb-3">
                  <v-btn
                    size="small"
                    color="success"
                    variant="flat"
                    block
                    :disabled="!canMarkAsPresent()"
                    @click="checkInAttendee(attendance)"
                  >
                    Check In Now
                  </v-btn>
                </div>

                <v-textarea
                  :model-value="attendance.notes"
                  label="Notes"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  hide-details
                  :disabled="!attendanceStatus?.can_manage_attendance"
                  @blur="updateAttendanceNotes(attendance, $event.target.value)"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Add Attendee Dialog -->
    <v-dialog v-model="showAddAttendeeDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6">
          Add Attendee
        </v-card-title>
        
        <v-card-text>
          <v-autocomplete
            v-model="selectedUser"
            :items="availableUsers"
            item-title="full_name"
            item-value="id"
            label="Select Member"
            variant="outlined"
            clearable
          >
            <template #item="{ props, item }">
              <v-list-item v-bind="props">
                <template #prepend>
                  <v-avatar size="40">
                    <v-img
                      v-if="item.raw.avatar"
                      :src="getUserPhotoUrl(item.raw)"
                      :alt="item.raw.full_name"
                    />
                    <span v-else>{{ getInitials(item.raw.full_name) }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ item.raw.full_name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.raw.email }}</v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showAddAttendeeDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedUser"
            :loading="addingAttendee"
            @click="addAttendee"
          >
            Add Attendee
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Attendance Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          Edit Attendance - {{ editingAttendance?.user?.full_name }}
        </v-card-title>
        
        <v-card-text>
          <v-form ref="editForm">
            <v-select
              v-model="editForm.status"
              :items="statusOptions"
              label="Status"
              variant="outlined"
              required
              class="mb-3"
            />

            <v-text-field
              v-model="editForm.checked_in_at"
              label="Check-in Time"
              type="datetime-local"
              variant="outlined"
              class="mb-3"
            />

            <v-textarea
              v-model="editForm.notes"
              label="Notes"
              variant="outlined"
              rows="3"
            />
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showEditDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="updatingAttendance"
            @click="saveAttendanceEdit"
          >
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Export Dialog -->
    <v-dialog v-model="showExportDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">mdi-export</v-icon>
          Export Attendance
        </v-card-title>
        
        <v-card-text>
          <div class="mb-4">
            <h3 class="text-subtitle-1 mb-2">Meeting Details</h3>
            <div class="text-body-2 text-medium-emphasis">
              <div><strong>Title:</strong> {{ meeting?.title }}</div>
              <div><strong>Date:</strong> {{ formatDate(meeting?.start_datetime) }}</div>
              <div><strong>Attendees:</strong> {{ attendance?.length || 0 }}</div>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <h3 class="text-subtitle-1 mb-3">Select Export Format</h3>
          <div class="d-flex flex-column gap-2">
            <v-btn
              variant="outlined"
              class="justify-start"
              prepend-icon="mdi-file-delimited"
              :loading="exporting"
              @click="performExport('csv')"
            >
              <div class="text-left">
                <div class="font-weight-medium">CSV Format</div>
                <div class="text-caption text-medium-emphasis">Comma-separated values for spreadsheet apps</div>
              </div>
            </v-btn>

            <v-btn
              variant="outlined"
              class="justify-start"
              prepend-icon="mdi-microsoft-excel"
              :loading="exporting"
              @click="performExport('excel')"
            >
              <div class="text-left">
                <div class="font-weight-medium">Excel Format</div>
                <div class="text-caption text-medium-emphasis">Microsoft Excel compatible format</div>
              </div>
            </v-btn>

            <v-btn
              variant="outlined"
              class="justify-start"
              prepend-icon="mdi-file-pdf-box"
              :loading="exporting"
              @click="performExport('pdf')"
            >
              <div class="text-left">
                <div class="font-weight-medium">PDF Report</div>
                <div class="text-caption text-medium-emphasis">Professional formatted report for printing</div>
              </div>
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="showExportDialog = false"
            :disabled="exporting"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useNotifications } from '@/composables/useNotifications'
import { dateFormat } from '@/utils/dateFormat'
import api, { API_BASE_URL } from '@/services/api'

export default {
  name: 'AttendanceManagement',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const { 
      showSuccess, 
      showError, 
      showOperationSuccess, 
      showOperationError,
      showAttendanceNotification 
    } = useNotifications()

    // API Configuration
    const apiBaseUrl = API_BASE_URL

    // State
    const loading = ref(true)
    const meeting = ref(null)
    const attendance = ref([])
    const allUsers = ref([])
    const searchQuery = ref('')
    const statusFilter = ref(null)
    const roleFilter = ref(null)
    const viewMode = ref('list')
    const attendanceStatus = ref(null)
    const closingAttendance = ref(false)

    // Dialog states
    const showAddAttendeeDialog = ref(false)
    const showEditDialog = ref(false)
    const showExportDialog = ref(false)
    const selectedUser = ref(null)
    const addingAttendee = ref(false)
    const editingAttendance = ref(null)
    const updatingAttendance = ref(false)
    const exporting = ref(false)

    // Form data
    const editForm = ref({
      status: '',
      checked_in_at: '',
      notes: ''
    })

    // Options
    const statusOptions = [
      { title: 'Invited', value: 'invited' },
      { title: 'Attending', value: 'attending' },
      { title: 'Absent', value: 'absent' },
      { title: 'Late', value: 'late' }
    ]

    const roleOptions = ref([])

    // Table headers
    const tableHeaders = [
      { title: 'Member', key: 'user', sortable: false },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Check-in Time', key: 'checked_in_at', sortable: true },
      { title: 'Notes', key: 'notes', sortable: false },
      { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
    ]

    // Computed
    const attendanceStats = computed(() => {
      const total = attendance.value.length
      const present = attendance.value.filter(att => 
        att.status === 'attending' || att.status === 'late'
      ).length
      const absent = attendance.value.filter(att => att.status === 'absent').length
      const rate = total > 0 ? (present / total) * 100 : 0

      return { total, present, absent, rate }
    })

    const filteredAttendance = computed(() => {
      let filtered = attendance.value

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(att =>
          att.user?.full_name?.toLowerCase().includes(query) ||
          att.user?.email?.toLowerCase().includes(query)
        )
      }

      if (statusFilter.value) {
        filtered = filtered.filter(att => att.status === statusFilter.value)
      }

      if (roleFilter.value) {
        filtered = filtered.filter(att => 
          att.user?.board_member?.position === roleFilter.value
        )
      }

      return filtered
    })

    const availableUsers = computed(() => {
      const currentAttendeeIds = attendance.value.map(att => att.user_id)
      return allUsers.value.filter(user => !currentAttendeeIds.includes(user.id))
    })

    const canManageAttendance = computed(() => {
      return attendanceStatus.value?.can_manage_attendance === true
    })

    const attendanceStatusDisplay = computed(() => {
      if (!attendanceStatus.value) return null
      
      if (attendanceStatus.value.attendance_closed) {
        return {
          type: 'closed',
          title: 'Attendance Closed',
          message: getAttendanceStatusMessage(),
          color: 'warning',
          icon: 'mdi-lock'
        }
      }
      
      if (meeting.value?.status === 'completed' && attendanceStatus.value.time_remaining_text) {
        return {
          type: 'warning',
          title: 'Auto-Close Warning',
          message: `Attendance will auto-close in ${attendanceStatus.value.time_remaining_text}`,
          color: 'info',
          icon: 'mdi-clock-alert-outline'
        }
      }
      
      return null
    })

    // Methods
    const formatDate = (dateString) => {
      return dateFormat.formatDate(dateString)
    }

    const formatTime = (dateString) => {
      return dateFormat.formatTime(dateString)
    }

    const formatDateTime = (dateString) => {
      return dateFormat.formatDateTime(dateString)
    }

    const canMarkAsPresent = () => {
      if (!meeting.value?.start_datetime) return false
      
      // Check if attendance is closed
      if (attendanceStatus.value && !attendanceStatus.value.can_manage_attendance) {
        return false
      }
      
      const now = new Date()
      const meetingStart = new Date(meeting.value.start_datetime)
      const meetingDate = new Date(meetingStart.toDateString())
      const today = new Date(now.toDateString())
      
      // Can only mark present on the day of meeting or after
      const isOnOrAfterMeetingDay = today >= meetingDate
      
      // If it's the meeting day, can only mark present after start time
      const canMarkToday = today.getTime() === meetingDate.getTime() ? now >= meetingStart : true
      
      return isOnOrAfterMeetingDay && canMarkToday
    }

    const getAttendanceStatusMessage = () => {
      // Check if attendance is closed
      if (attendanceStatus.value && attendanceStatus.value.attendance_closed) {
        const closedAt = attendanceStatus.value.attendance_closed_at ? 
          formatDateTime(attendanceStatus.value.attendance_closed_at) : 'Unknown time'
        return `Attendance management was closed on ${closedAt}`
      }
      
      if (attendanceStatus.value && !attendanceStatus.value.can_manage_attendance && meeting.value?.status === 'completed') {
        return 'Attendance management has been automatically closed (48 hours after meeting completion)'
      }
      
      if (!meeting.value?.start_datetime) return ''
      
      const now = new Date()
      const meetingStart = new Date(meeting.value.start_datetime)
      const meetingDate = new Date(meetingStart.toDateString())
      const today = new Date(now.toDateString())
      
      if (today < meetingDate) {
        return `Attendance can only be marked on ${formatDate(meeting.value.start_datetime)} after ${formatTime(meeting.value.start_datetime)}`
      } else if (today.getTime() === meetingDate.getTime() && now < meetingStart) {
        return `Attendance can be marked after ${formatTime(meeting.value.start_datetime)}`
      }
      
      return ''
    }

    const getFilteredStatusOptions = (currentStatus) => {
      const baseOptions = statusOptions.filter(option => {
        // Always allow changing to invited or absent
        if (option.value === 'invited' || option.value === 'absent') return true
        
        // For attending or late, check if it's allowed based on meeting time
        if (option.value === 'attending' || option.value === 'late') {
          return canMarkAsPresent()
        }
        
        return true
      })
      
      // Always include current status even if it wouldn't normally be allowed
      // (in case someone was already marked present before the restriction)
      if (currentStatus && !baseOptions.find(opt => opt.value === currentStatus)) {
        const currentOption = statusOptions.find(opt => opt.value === currentStatus)
        if (currentOption) {
          baseOptions.push(currentOption)
        }
      }
      
      return baseOptions
    }

    const getInitials = (name) => {
      if (!name) return 'N/A'
      return name.split(' ').map(part => part[0]).join('').toUpperCase()
    }

    const getUserPhotoUrl = (user) => {
      if (!user?.avatar) return null
      return `${apiBaseUrl}/members/${user.id}/photo?t=${Date.now()}`
    }

    const getStatusColor = (status) => {
      const colors = {
        invited: 'grey',
        attending: 'success',
        absent: 'error',
        late: 'warning'
      }
      return colors[status] || 'grey'
    }

    const loadMeetingData = async () => {
      loading.value = true
      try {
        // Load meeting details
        const meetingResponse = await api.get(`/meetings/${route.params.id}`)
        meeting.value = meetingResponse.data.meeting
        attendance.value = meeting.value.attendance || []

        // Load attendance status
        await loadAttendanceStatus()

        // Load all users for adding attendees
        const usersResponse = await api.get('/auth/users?per_page=1000')
        allUsers.value = usersResponse.data.users || []

        // Extract unique roles for filtering
        const roles = [...new Set(
          allUsers.value
            .filter(user => user.board_member?.position)
            .map(user => user.board_member.position)
        )]
        roleOptions.value = roles.map(role => ({ title: role, value: role }))

      } catch (error) {
        console.error('Error loading meeting data:', error)
        showError('Failed to load meeting data')
      } finally {
        loading.value = false
      }
    }

    const updateAttendanceStatus = async (attendanceRecord, newStatus) => {
      // Validate if marking as present is allowed
      if ((newStatus === 'attending' || newStatus === 'late') && !canMarkAsPresent()) {
        showError(getAttendanceStatusMessage() || 'Cannot mark as present yet')
        return
      }

      try {
        const response = await api.put(`/meetings/${meeting.value.id}/attendance/${attendanceRecord.id}`, {
          status: newStatus
        })
        
        // Update local data
        const index = attendance.value.findIndex(att => att.id === attendanceRecord.id)
        if (index !== -1) {
          attendance.value[index] = response.data.attendance
        }
        
        showAttendanceNotification('confirmed', attendanceRecord.user.full_name, meeting.value?.title)
      } catch (error) {
        console.error('Error updating attendance:', error)
        showOperationError('update attendance status', error)
      }
    }

    const updateAttendanceNotes = async (attendanceRecord, notes) => {
      if (attendanceRecord.notes === notes) return

      try {
        await api.put(`/meetings/${meeting.value.id}/attendance/${attendanceRecord.id}`, {
          notes: notes
        })
        
        // Update local data
        attendanceRecord.notes = notes
        showSuccess('Notes updated')
      } catch (error) {
        console.error('Error updating notes:', error)
        showError('Failed to update notes')
      }
    }

    const checkInAttendee = async (attendanceRecord) => {
      // Validate if check-in is allowed
      if (!canMarkAsPresent()) {
        showError(getAttendanceStatusMessage() || 'Cannot check in yet - meeting has not started')
        return
      }

      try {
        const response = await api.put(`/meetings/${meeting.value.id}/attendance/${attendanceRecord.id}`, {
          status: 'attending',
          checked_in_at: new Date().toISOString()
        })
        
        // Update local data
        const index = attendance.value.findIndex(att => att.id === attendanceRecord.id)
        if (index !== -1) {
          attendance.value[index] = response.data.attendance
        }
        
        showAttendanceNotification('checked-in', attendanceRecord.user.full_name, meeting.value?.title)
      } catch (error) {
        console.error('Error checking in attendee:', error)
        showOperationError('check in attendee', error)
      }
    }

    const addAttendee = async () => {
      if (!selectedUser.value) return

      addingAttendee.value = true
      try {
        const response = await api.post(`/meetings/${meeting.value.id}/attendance`, {
          user_id: selectedUser.value,
          status: 'invited'
        })
        
        attendance.value.push(response.data.attendance)
        showAddAttendeeDialog.value = false
        selectedUser.value = null
        showOperationSuccess('Add attendee', `${selectedUser.value} has been added to the meeting`)
      } catch (error) {
        console.error('Error adding attendee:', error)
        showOperationError('add attendee', error)
      } finally {
        addingAttendee.value = false
      }
    }

    const editAttendance = (attendanceRecord) => {
      editingAttendance.value = attendanceRecord
      editForm.value = {
        status: attendanceRecord.status,
        checked_in_at: attendanceRecord.checked_in_at ? 
          new Date(attendanceRecord.checked_in_at).toISOString().slice(0, 16) : '',
        notes: attendanceRecord.notes || ''
      }
      showEditDialog.value = true
    }

    const saveAttendanceEdit = async () => {
      updatingAttendance.value = true
      try {
        const data = {
          status: editForm.value.status,
          notes: editForm.value.notes
        }

        if (editForm.value.checked_in_at) {
          data.checked_in_at = new Date(editForm.value.checked_in_at).toISOString()
        }

        const response = await api.put(
          `/meetings/${meeting.value.id}/attendance/${editingAttendance.value.id}`,
          data
        )
        
        // Update local data
        const index = attendance.value.findIndex(att => att.id === editingAttendance.value.id)
        if (index !== -1) {
          attendance.value[index] = response.data.attendance
        }
        
        showEditDialog.value = false
        showAttendanceNotification(
          'updated', 
          selectedAttendance.value?.user?.full_name,
          meeting.value?.title
        )
      } catch (error) {
        console.error('Error updating attendance:', error)
        showOperationError('update attendance', error)
      } finally {
        updatingAttendance.value = false
      }
    }

    const removeAttendee = async (attendanceRecord) => {
      if (!confirm(`Remove ${attendanceRecord.user?.full_name} from this meeting?`)) return

      try {
        await api.delete(`/meetings/${meeting.value.id}/attendance/${attendanceRecord.id}`)
        
        // Remove from local data
        const index = attendance.value.findIndex(att => att.id === attendanceRecord.id)
        if (index !== -1) {
          attendance.value.splice(index, 1)
        }
        
        showSuccess('Attendee removed successfully')
      } catch (error) {
        console.error('Error removing attendee:', error)
        showError('Failed to remove attendee')
      }
    }

    const bulkMarkPresent = async () => {
      // Validate if bulk marking as present is allowed
      if (!canMarkAsPresent()) {
        showError(getAttendanceStatusMessage() || 'Cannot mark attendees as present yet')
        return
      }

      if (!confirm('Mark all attendees as present?')) return

      try {
        const promises = attendance.value
          .filter(att => att.status !== 'attending')
          .map(att => 
            api.put(`/meetings/${meeting.value.id}/attendance/${att.id}`, {
              status: 'attending'
            })
          )
        
        await Promise.all(promises)
        await loadMeetingData() // Reload to get updated data
        showSuccess('All attendees marked as present')
      } catch (error) {
        console.error('Error bulk updating attendance:', error)
        showError('Failed to update attendance')
      }
    }

    const exportAttendance = () => {
      showExportDialog.value = true
    }

    const performExport = async (format) => {
      try {
        exporting.value = true
        
        // Prepare attendance data for export
        const exportData = {
          meeting: {
            title: meeting.value.title,
            date: formatDate(meeting.value.start_datetime),
            time: formatTime(meeting.value.start_datetime) + ' - ' + formatTime(meeting.value.end_datetime),
            location: meeting.value.location || 'N/A',
            type: meeting.value.meeting_type?.name || 'N/A',
            status: meeting.value.status
          },
          attendance: attendance.value.map(record => ({
            name: record.user?.full_name || record.member?.full_name || 'Unknown',
            email: record.user?.email || record.member?.email || 'N/A',
            position: record.user?.board_member?.position || 'N/A',
            status: record.status,
            checkedInAt: record.checked_in_at ? formatDateTime(record.checked_in_at) : 'N/A',
            notes: record.notes || ''
          })),
          summary: {
            totalInvited: attendance.value.length,
            attending: attendance.value.filter(a => a.status === 'attending').length,
            absent: attendance.value.filter(a => a.status === 'absent').length,
            invited: attendance.value.filter(a => a.status === 'invited').length,
            late: attendance.value.filter(a => a.status === 'late').length,
            exportedAt: new Date().toISOString(),
            exportedBy: 'Current User' // TODO: Get from auth store
          }
        }

        if (format === 'csv') {
          exportToCSV(exportData)
        } else if (format === 'excel') {
          await exportToExcel(exportData)
        } else if (format === 'pdf') {
          await exportToPDF(exportData)
        }
        
        showExportDialog.value = false
        showSuccess(`Attendance exported successfully as ${format.toUpperCase()}`)
      } catch (error) {
        console.error('Export error:', error)
        showError('Failed to export attendance data')
      } finally {
        exporting.value = false
      }
    }

    const exportToCSV = (data) => {
      const csvContent = []
      
      // Add header information
      csvContent.push('Meeting Attendance Report')
      csvContent.push('')
      csvContent.push(`Meeting: ${data.meeting.title}`)
      csvContent.push(`Date: ${data.meeting.date}`)
      csvContent.push(`Time: ${data.meeting.time}`)
      csvContent.push(`Location: ${data.meeting.location}`)
      csvContent.push(`Type: ${data.meeting.type}`)
      csvContent.push(`Status: ${data.meeting.status}`)
      csvContent.push('')
      
      // Add summary
      csvContent.push('Summary:')
      csvContent.push(`Total Invited: ${data.summary.totalInvited}`)
      csvContent.push(`Attending: ${data.summary.attending}`)
      csvContent.push(`Absent: ${data.summary.absent}`)
      csvContent.push(`Invited: ${data.summary.invited}`)
      csvContent.push(`Late: ${data.summary.late}`)
      csvContent.push('')
      
      // Add attendance data headers
      csvContent.push('Name,Email,Position,Status,Check In Time,Notes')
      
      // Add attendance data
      data.attendance.forEach(record => {
        csvContent.push(`"${record.name}","${record.email}","${record.position}","${record.status}","${record.checkedInAt}","${record.notes}"`)
      })
      
      csvContent.push('')
      csvContent.push(`Exported: ${new Date().toLocaleString()}`)
      csvContent.push(`Exported by: ${data.summary.exportedBy}`)
      
      // Create and download file
      const csvString = csvContent.join('\\n')
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `attendance_${data.meeting.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }

    const exportToExcel = async (data) => {
      // For Excel export, we'll use a library like xlsx or create a more structured CSV
      // For now, creating an enhanced CSV with better formatting
      const csvContent = []
      
      // Meeting Information Sheet
      csvContent.push('MEETING ATTENDANCE REPORT')
      csvContent.push(',')
      csvContent.push('Meeting Information,')
      csvContent.push(`Title,${data.meeting.title}`)
      csvContent.push(`Date,${data.meeting.date}`)
      csvContent.push(`Time,${data.meeting.time}`)
      csvContent.push(`Location,${data.meeting.location}`)
      csvContent.push(`Type,${data.meeting.type}`)
      csvContent.push(`Status,${data.meeting.status}`)
      csvContent.push(',')
      
      // Summary Sheet
      csvContent.push('Attendance Summary,')
      csvContent.push(`Total Invited,${data.summary.totalInvited}`)
      csvContent.push(`Attending,${data.summary.attending}`)
      csvContent.push(`Absent,${data.summary.absent}`)
      csvContent.push(`Invited,${data.summary.invited}`)
      csvContent.push(`Late,${data.summary.late}`)
      csvContent.push(',')
      
      // Detailed Attendance
      csvContent.push('Detailed Attendance,')
      csvContent.push('Name,Email,Position,Status,Check In Time,Notes')
      
      data.attendance.forEach(record => {
        csvContent.push(`"${record.name}","${record.email}","${record.position}","${record.status}","${record.checkedInAt}","${record.notes}"`)
      })
      
      csvContent.push(',')
      csvContent.push(`Exported,${new Date().toLocaleString()}`)
      csvContent.push(`Exported by,${data.summary.exportedBy}`)
      
      // Create and download file
      const csvString = csvContent.join('\\n')
      const blob = new Blob([csvString], { type: 'application/vnd.ms-excel;charset=utf-8;' })
      const link = document.createElement('a')
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `attendance_${data.meeting.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.xls`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }

    const exportToPDF = async (data) => {
      // Create HTML content for PDF
      const htmlContent = `
        <html>
        <head>
          <title>Meeting Attendance Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { color: #1976d2; margin: 0; }
            .header p { margin: 5px 0; color: #666; }
            .meeting-info { background: #f5f5f5; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
            .meeting-info h3 { margin-top: 0; color: #1976d2; }
            .summary { display: flex; justify-content: space-around; margin: 20px 0; }
            .summary-item { text-align: center; }
            .summary-item .number { font-size: 24px; font-weight: bold; color: #1976d2; }
            .summary-item .label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #1976d2; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .status-attending { color: #4caf50; font-weight: bold; }
            .status-absent { color: #f44336; font-weight: bold; }
            .status-invited { color: #ff9800; font-weight: bold; }
            .status-late { color: #673ab7; font-weight: bold; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Meeting Attendance Report</h1>
            <p>${data.meeting.title}</p>
            <p>${data.meeting.date} | ${data.meeting.time}</p>
          </div>
          
          <div class="meeting-info">
            <h3>Meeting Information</h3>
            <p><strong>Location:</strong> ${data.meeting.location}</p>
            <p><strong>Type:</strong> ${data.meeting.type}</p>
            <p><strong>Status:</strong> ${data.meeting.status}</p>
          </div>
          
          <div class="summary">
            <div class="summary-item">
              <div class="number">${data.summary.totalInvited}</div>
              <div class="label">Total Invited</div>
            </div>
            <div class="summary-item">
              <div class="number">${data.summary.attending}</div>
              <div class="label">Attending</div>
            </div>
            <div class="summary-item">
              <div class="number">${data.summary.absent}</div>
              <div class="label">Absent</div>
            </div>
            <div class="summary-item">
              <div class="number">${data.summary.invited}</div>
              <div class="label">Invited</div>
            </div>
            <div class="summary-item">
              <div class="number">${data.summary.late}</div>
              <div class="label">Late</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Position</th>
                <th>Status</th>
                <th>Check In</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${data.attendance.map(record => `
                <tr>
                  <td>${record.name}</td>
                  <td>${record.position}</td>
                  <td class="status-${record.status}">${record.status.toUpperCase()}</td>
                  <td>${record.checkedInAt}</td>
                  <td>${record.notes}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Report generated on ${new Date().toLocaleString()}</p>
            <p>Exported by: ${data.summary.exportedBy}</p>
          </div>
        </body>
        </html>
      `
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank')
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      
      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print()
        setTimeout(() => {
          printWindow.close()
        }, 1000)
      }
    }

    const loadAttendanceStatus = async () => {
      try {
        const response = await api.get(`/meetings/${route.params.id}/attendance-status`)
        attendanceStatus.value = response.data
      } catch (error) {
        console.error('Error loading attendance status:', error)
      }
    }

    const closeAttendance = async () => {
      if (!confirm('Are you sure you want to close attendance management? This action cannot be undone.')) {
        return
      }

      closingAttendance.value = true
      try {
        const response = await api.post(`/meetings/${route.params.id}/close-attendance`)
        
        // Update local meeting data
        meeting.value = response.data.meeting
        attendanceStatus.value.attendance_closed = true
        attendanceStatus.value.can_manage_attendance = false
        
        showSuccess('Attendance management closed successfully')
        await loadAttendanceStatus() // Reload status
      } catch (error) {
        console.error('Error closing attendance:', error)
        showError('Failed to close attendance management')
      } finally {
        closingAttendance.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadMeetingData()
    })

    return {
      // State
      loading,
      meeting,
      attendance,
      allUsers,
      searchQuery,
      statusFilter,
      roleFilter,
      viewMode,
      attendanceStatus,
      closingAttendance,

      // Dialog states
      showAddAttendeeDialog,
      showEditDialog,
      showExportDialog,
      selectedUser,
      addingAttendee,
      editingAttendance,
      updatingAttendance,
      exporting,

      // Form data
      editForm,

      // Options
      statusOptions,
      roleOptions,
      tableHeaders,

      // Computed
      attendanceStats,
      filteredAttendance,
      availableUsers,
      canManageAttendance,
      attendanceStatusDisplay,

      // Methods
      formatDate,
      formatTime,
      formatDateTime,
      getInitials,
      getUserPhotoUrl,
      getStatusColor,
      canMarkAsPresent,
      getAttendanceStatusMessage,
      getFilteredStatusOptions,
      updateAttendanceStatus,
      updateAttendanceNotes,
      checkInAttendee,
      addAttendee,
      editAttendance,
      saveAttendanceEdit,
      removeAttendee,
      bulkMarkPresent,
      exportAttendance,
      performExport,
      exportToCSV,
      exportToExcel,
      exportToPDF,
      loadAttendanceStatus,
      closeAttendance
    }
  }
}
</script>

<style scoped lang="scss">
.attendance-management-page {
  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .text-medium-emphasis {
    opacity: 0.7;
  }

  .page-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    padding-bottom: 24px;
  }

  .summary-card {
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }
  }

  .attendance-card {
    transition: transform 0.2s, box-shadow 0.2s;
    border-left: 4px solid transparent;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
    }

    &.border-success {
      border-left-color: rgb(var(--v-theme-success));
    }

    &.border-error {
      border-left-color: rgb(var(--v-theme-error));
    }

    &.border-warning {
      border-left-color: rgb(var(--v-theme-warning));
    }
  }

  .attendance-grid {
    .v-col {
      transition: all 0.3s ease;
    }
  }
}
</style>